import {compose} from "mu-lambda";
import * as React from "react";
import {Redirect} from "react-router-dom";

import {useFlagOrCookie} from "@khan/analytics-growthbook";

import PageRoot from "#app-shell-package/page-root.tsx";
import RouteLoadSpinner from "#app-shell-package/route-load-spinner.tsx";
import withPageDisplay, {
    type WithoutPageDisplay,
    type WithPageDisplayProps,
} from "#data-access/with-page-display.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";

import PublicClassroomInfoByClassCodeQuery from "../../graphql/public-classroom-info-by-class-code-query.tsx";

import JoinWidget from "./join-widget.tsx";
import {withStrings} from "./locales/join-class-page/load-strings.tsx";

// Since JoinClassPage is a class component and hooks can't be used here,
// we wrap it with withTosFlag HOC to inject the feature flag as a prop.
function withTosFlag<P>(
    Component: React.ComponentType<P & {tosFlag: boolean}>,
) {
    return function Wrapper(props: P) {
        const tosFlag = useFlagOrCookie("signup-tos-checkbox-required");
        return <Component {...props} tosFlag={tosFlag} />;
    };
}

type Props = {
    classCode?: string;
    tosFlag?: boolean;
} & WithPageDisplayProps &
    WithUserProfileProps;

/**
 * JoinClassPage is the App Shell page for Join By Class Code, at /join!
 *
 * This component handles high-level data fetching and redirect conditions; the
 * UI and core business logic starts one level down, in JoinWidget!
 */
class JoinClassPage extends React.Component<Props> {
    render(): React.ReactNode {
        const {classCode, profile, profileLoading} = this.props;

        // This is the most common case: no class code, just render the page!
        if (!classCode) {
            return (
                <PageRoot>
                    <JoinWidget tosFlag={this.props.tosFlag} />
                </PageRoot>
            );
        }

        // But if a class code is provided, we to load some data and check things!
        return (
            <PageRoot>
                <PublicClassroomInfoByClassCodeQuery classCode={classCode}>
                    {({data, loading, error}) => {
                        if (loading || profileLoading) {
                            // TODO(mdr): Would be nicer to render the full page layout
                            //     with the spinner just in the content area, but a bit
                            //     annoying to do with the current shape of the code!
                            return <RouteLoadSpinner />;
                        }

                        const classroomInfo = data?.joinByClassCodeInfoV2;

                        const className = classroomInfo && classroomInfo.name;
                        const includesCentrallyOwnedDistrictData =
                            classroomInfo &&
                            classroomInfo.includesCentrallyOwnedDistrictData;

                        if (error || className == null) {
                            // Don't be loud about the error in this case, just let the
                            // user type in their code to recover.
                            return <JoinWidget tosFlag={this.props.tosFlag} />;
                        }

                        // If we successfully loaded a class, but the user doesn't have
                        // the account permission to join classes in general, redirect
                        // them to a special error message!
                        if (profile && !profile.get("canModifyCoaches")) {
                            return (
                                <Redirect
                                    to={`/coaches?restrictedClassName=${className}`}
                                />
                            );
                        }

                        return (
                            <JoinWidget
                                classSignupCode={classCode}
                                className={className}
                                classIncludesCentrallyOwnedDistrictData={
                                    includesCentrallyOwnedDistrictData || false
                                }
                                tosFlag={this.props.tosFlag}
                            />
                        );
                    }}
                </PublicClassroomInfoByClassCodeQuery>
            </PageRoot>
        );
    }
}

type ExportProps = WithoutPageDisplay<
    WithoutUserProfile<
        JSX.LibraryManagedAttributes<
            typeof JoinClassPage,
            React.ComponentProps<typeof JoinClassPage>
        >
    >
>;

export default compose(
    withStrings,
    withPageDisplay({
        noIndex: true,
    }),
    withUserProfile,
    withTosFlag,
)(JoinClassPage) as React.ComponentType<ExportProps>;
